import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ArrowDirection, Cell, DefinitionCell, DefinitionClue, LetterCell, Position, Puzzle } from '@/domain';
import { useAnnouncer } from '@/ui/components/a11y/Announcer';
import { t } from '@/ui/i18n';
import { wordRange } from './wordRange';

// 'across' === ArrowDirection 'right'; 'down' === 'down'.
export type Direction = 'across' | 'down';

export interface Clue {
  readonly definition: DefinitionCell;
  readonly clue: DefinitionClue;
  readonly direction: Direction;
  readonly cells: readonly LetterCell[];
}

export interface CellHighlight {
  readonly currentWord: boolean;
  // True when this cell is the React-state-focused cell. Drives the
  // solo-mode focused-cell ring on the wrapper so the visual persists
  // when DOM focus leaves the input (e.g. user taps the hint button or
  // page chrome). `false` for all cells whenever no cell is focused.
  readonly focused: boolean;
  // Arrow direction of the active clue when the focused cell sits on a
  // path that originates at this definition cell; `null` otherwise. The
  // renderer uses this to highlight the matching sub-clue inside a
  // stacked definition cell (ADR-0005 §3a).
  readonly currentArrow: ArrowDirection | null;
}

export interface GridNavigation {
  readonly registerCellRef: (el: HTMLInputElement | null) => void;
  readonly highlightFor: (position: Position) => CellHighlight;
  // click (not pointerdown) — pan gestures never produce a click, so focus is suppressed naturally.
  readonly handleClick: (event: React.MouseEvent<HTMLDivElement>) => void;
  // Tap a definition cell → focus its word's first editable-and-empty cell; a repeat tap on a dual-def cell toggles to its other clue. Pan-gated.
  readonly handleDefinitionClick: (position: Position) => void;
  readonly handleFocus: (event: React.FocusEvent<HTMLInputElement>) => void;
  // Snap DOM focus back to the cell input when blur would otherwise leave
  // the grid for a button / non-focusable element / page background. Keeps
  // typing reachable after a tap on the hint button or page chrome — on
  // mobile this also re-shows the soft keyboard. No React state writes;
  // `focused` is already sticky.
  readonly handleBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  readonly handleKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  // Android Gboard fires keydown key==="Unidentified"; the real letter arrives here via InputEvent.data.
  readonly handleInput: (event: React.FormEvent<HTMLInputElement>) => void;
  // Active clue under the focused cell, or null when nothing is focused.
  // Surfaced so the grid container can render the full clue text in a
  // panel outside the grid (cells truncate the prose at small font sizes).
  readonly currentClue: Clue | null;
  // Position of the focused cell within the active clue's `cells` array
  // (0-based) — the panel renders this as the "2 / 5" progress count.
  // Null whenever `currentClue` is null.
  readonly currentClueIndex: number | null;
  // The OTHER clue at the focused intersection (the one the user could
  // switch to with space / a tap). Null when the cell sits on only one
  // clue path. Drives the alt-clue chip in the current-clue panel
  // (ADR-0005 §6 redesign — cell-at-intersection state).
  readonly alternateClue: Clue | null;
  // Flips the active solving direction at the focused cell. Surfaced
  // for the panel's alt-clue button — clicking it should switch
  // direction without touching focus, same effect as a re-tap on an
  // intersection cell.
  readonly toggleDirection: () => void;
  readonly enterLetter: (char: string) => void;
  readonly eraseLetter: () => void;
  readonly cycleClue: (step: 1 | -1) => void;
  // Imperative cursor step; same semantics as the physical ArrowX handlers (flip-then-step).
  readonly moveCursor: (direction: 'left' | 'right' | 'up' | 'down') => void;
  // True when (row, col) is in the validation set; always false when no validator is wired.
  readonly isCellValidated: (row: number, col: number) => boolean;
  // Reads the player's current letter at (row, col) — '' when the cell
  // is empty. The callback identity changes whenever any letter
  // changes (per the version counter inside the hook), so React
  // re-renders consumers of the panel's letter previews on every
  // typed / cleared character.
  readonly getEntryAt: (row: number, col: number) => string;
  // The local user's current cursor — `null` when nothing is focused.
  // Mirrors the data the outbound `onLocalFocusChange` callback already
  // emits, but synchronously available for local rendering (presence map
  // composition, the multiplayer cursor highlight pipeline, etc).
  readonly localCursor: {
    readonly position: Position;
    readonly direction: Direction;
  } | null;
  // Inbound multiplayer write — apply a remote `cellUpdated` event to the
  // uncontrolled <input> at (row, col). Writes `letter ?? ''` directly to
  // `el.value` (per ADR-0002 §4: cell values live in the DOM), updates
  // the same per-cell mirror that handleInput/handleKeyDown use so a
  // subsequent local same-letter no-op is detected, and DOES NOT fire
  // `onCellChange` (the inbound path must never re-emit and create an
  // echo loop). No focus or direction change — remote writes by other
  // players must not yank the local user's caret. Used by `Grid` (Wave H
  // PR #19) when wired to the WebSocket cellUpdated stream. No-op if the
  // target cell is not a registered letter input (out-of-bounds writes
  // from a stale broadcast are ignored).
  readonly applyRemoteCellUpdate: (row: number, col: number, letter: string | null) => void;
  // Full clue set for renderers outside the focused-cell path (e.g. the compound-word hyphen overlay).
  readonly allClues: readonly Clue[];
}

// Divergences from NYT-app behavior:
// - Typing past the last cell of a word stays put (no auto-advance to
//   the next clue). Backspace at the start of a word same — no surprise
//   clue jumps.
// - Tab / Enter cycle to the NEXT word's first cell (Shift+Tab /
//   Shift+Enter walk backward); both wrap. The clue order is
//   across-then-down, sub-sorted by the starting cell's (row, col) — so
//   the cycle traverses every across answer top-to-bottom, then every
//   down answer top-to-bottom, then wraps. Mobile keyboards' "Next"
//   button maps to Enter, so the same path serves the
//   `enterKeyHint="next"` affordance for soft keyboards.

const key = (p: Position) => `${p.row},${p.col}`;
const same = (a: Position | null, b: Position | null) =>
  a !== null && b !== null && a.row === b.row && a.col === b.col;
const posOf = (el: HTMLElement): Position | null => {
  const r = el.dataset.row, c = el.dataset.col;
  return r === undefined || c === undefined ? null : { row: Number(r), col: Number(c) };
};
// ASCII A–Z plus French diacritics the puzzle ships with.
const LETTER_RE = /^[a-zA-ZàâçéèêëîïôûùüÿñæœÀÂÇÉÈÊËÎÏÔÛÙÜŸÑÆŒ]$/;

// True when `p` is at least the second letter of `clue` AND every cell
// strictly before `p` carries a filled value in `values`. Returns false
// when `p` is the clue's first cell (idx 0 — no real progress to
// detect; the structural-start case is handled by the `starting` tier
// in handleClick) and when `p` is not part of `clue.cells` at all
// (defensive — callers should only pass clues actually containing `p`).
const prefixFilled = (
  clue: Clue,
  p: Position,
  values: Map<string, string>,
): boolean => {
  const idx = clue.cells.findIndex((c) => same(c.position, p));
  if (idx <= 0) return false;
  for (let i = 0; i < idx; i++) {
    if (!values.has(key(clue.cells[i].position))) return false;
  }
  return true;
};

// Display-form normalization for a single accepted keystroke. Strips
// combining diacritics (NFD then drops U+0300–U+036F) and uppercases
// — so 'é' renders as 'E', 'ç' as 'C', etc. Mots fléchés grids are
// stored unaccented (matches the wire form `normalizeAnswerLetter`
// produces for validation), and players type with their native AZERTY
// keyboard which emits accented glyphs. Ligatures (Æ/Œ) don't decompose
// under NFD and are preserved as-is — that's a pre-existing latent
// case the validation layer already rejects, kept untouched here.
const stripDiacritics = (letter: string): string =>
  letter.toUpperCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

interface ClueLookup {
  cellAt: (r: number, c: number) => Cell | null;
  cluesAt: (r: number, c: number) => readonly Clue[];
  clueAt: (r: number, c: number, d: Direction) => Clue | undefined;
  // Clues whose DEFINITION cell sits at (r,c), in tuple order (byCell is keyed on letter cells only).
  cluesByDefCell: (r: number, c: number) => readonly Clue[];
  // All clues in deterministic puzzle order: across-then-down, each
  // group sub-sorted by (row, col) of the starting cell. This is the
  // order Tab / Enter walks through. Built once per puzzle.
  readonly orderedClues: readonly Clue[];
}

// Index built once per puzzle (memo key = puzzle ref). Build O(N + sum
// of clue lengths); lookups O(1).
function buildLookup(puzzle: Puzzle): ClueLookup {
  const byPos = new Map<string, Cell>();
  for (const c of puzzle.cells) byPos.set(key(c.position), c);
  const byCell = new Map<string, Clue[]>();
  const byDef = new Map<string, Clue[]>();
  const allClues: Clue[] = [];
  const defs = puzzle.cells
    .filter((c): c is DefinitionCell => c.kind === 'definition')
    .slice()
    .sort((a, b) => a.position.row - b.position.row || a.position.col - b.position.col);
  for (const def of defs) {
    // A definition cell carries one or two clues per ADR-0005 §3a.
    // Each clue gets its own answer-path walk and own index entry.
    for (const subClue of def.clues) {
      // Start offset: where the first letter sits relative to the clue.
      // Step: direction letters flow after the first.
      const startDr = (subClue.arrow === 'down' || subClue.arrow === 'down-right') ? 1 : 0;
      const startDc = (subClue.arrow === 'right' || subClue.arrow === 'right-down') ? 1 : 0;
      const dr = (subClue.arrow === 'down' || subClue.arrow === 'right-down') ? 1 : 0;
      const dc = (subClue.arrow === 'right' || subClue.arrow === 'down-right') ? 1 : 0;
      const cells: LetterCell[] = [];
      let row = def.position.row + startDr, col = def.position.col + startDc;
      while (row >= 0 && row < puzzle.height && col >= 0 && col < puzzle.width) {
        const next = byPos.get(key({ row, col }));
        if (!next || next.kind !== 'letter') break;
        cells.push(next);
        row += dr; col += dc;
      }
      if (cells.length === 0) continue;
      const clue: Clue = {
        definition: def,
        clue: subClue,
        direction: (subClue.arrow === 'right' || subClue.arrow === 'down-right') ? 'across' : 'down',
        cells,
      };
      for (const cell of cells) {
        const k = key(cell.position);
        const list = byCell.get(k) ?? [];
        list.push(clue);
        byCell.set(k, list);
      }
      allClues.push(clue);
      const dk = key(def.position);
      const dlist = byDef.get(dk) ?? [];
      dlist.push(clue);
      byDef.set(dk, dlist);
    }
  }
  // Deterministic ordering for Tab / Enter cycling: by starting cell in
  // row-major order (row, then col). Across before down as a stable
  // tiebreak when two clues share a start cell (unusual in standard
  // grid geometry, but a safe default).
  const orderedClues: readonly Clue[] = allClues.slice().sort((a, b) => {
    const ar = a.cells[0].position, br = b.cells[0].position;
    if (ar.row !== br.row) return ar.row - br.row;
    if (ar.col !== br.col) return ar.col - br.col;
    if (a.direction !== b.direction) return a.direction === 'across' ? -1 : 1;
    return 0;
  });
  return {
    cellAt: (r, c) => byPos.get(key({ row: r, col: c })) ?? null,
    cluesAt: (r, c) => byCell.get(key({ row: r, col: c })) ?? [],
    clueAt: (r, c, d) => (byCell.get(key({ row: r, col: c })) ?? []).find((q) => q.direction === d),
    cluesByDefCell: (r, c) => byDef.get(key({ row: r, col: c })) ?? [],
    orderedClues,
  };
}

// Scroll-into-visible-viewport timing. We wait for the soft keyboard to
// finish opening before measuring the cell against `window.visualViewport`;
// otherwise the keyboard hasn't shrunk the visual viewport yet and our
// "is the cell visible?" math reads stale dimensions. 250 ms is long
// enough on Android Gboard / iOS Safari (both ~150–250 ms keyboard
// animations) and short enough that the user perceives the scroll as a
// natural reveal rather than a delayed jump. Subsequent focus events
// (typing → auto-advance) cancel the pending scroll, so only the last
// focused cell in a typing burst is considered.
const SCROLL_DELAY_MS = 250;
// Top margin = sticky panel min-height (~2.5rem) + breathing room so the
// cell lands clear of the panel and any focus ring it draws.
const SCROLL_TOP_MARGIN_PX = 64;
// Bottom margin keeps the cell off the visual-viewport bottom edge,
// which sometimes overlaps with the keyboard's accessory bar.
const SCROLL_BOTTOM_MARGIN_PX = 24;

export interface UseGridNavigationOptions {
  // Fires whenever a cell value transitions old → new. `letter` is the
  // post-normalization (uppercased) value or `null` when the cell was
  // cleared. Solo callers omit this; multiplayer callers wire it to the
  // WebSocket cellUpdate broadcast (Wave H · PR #19, see ADR-0018).
  readonly onCellChange?: (row: number, col: number, letter: string | null) => void;
  // Fires when a letter is placed; per-word callers (minigame, coop) detect word completion — solo omits this and validates the whole grid instead.
  readonly onCellFilled?: (
    position: Position,
    direction: 'across' | 'down',
  ) => void;
  // Fires whenever the focused cell or solving direction changes. The
  // multiplayer route wires this to `gameClient.cellFocus(...)` so peers
  // can render a coloured cursor + word tint at the focused position
  // (ADR-0018 §"Presence"). Adapter-side debounce (200 ms) collapses
  // bursts of focus changes into one outbound frame, so we fire on
  // every transition without filtering. Solo callers omit this and the
  // hook never schedules an out-of-band call.
  readonly onFocusChange?: (
    position: Position | null,
    direction: 'across' | 'down' | null,
  ) => void;
  // Returns the current zoom scale of the grid's `react-zoom-pan-pinch`
  // wrapper (1 = unzoomed). When `> 1.01`, `scheduleVisibleScroll`
  // bails out: a zoomed-in user has explicitly taken control of the
  // viewport and any auto-scroll-into-view fights the gesture. Read
  // from `transformWrapperRef.current?.state.scale` at the call site —
  // we accept a getter (not a value) because the library mutates its
  // state object in place, so a snapshot taken at hook-options time
  // would be stale. Solo callers wired without zoom can omit this and
  // the guard becomes a no-op (scale defaults to 1). Note: an earlier
  // attempt (PR #175) used `window.visualViewport.scale` instead, but
  // the library drives a JS-CSS transform — `visualViewport.scale`
  // stays at 1 throughout pinch, so the guard never fired.
  readonly getZoomScale?: () => number;
  // Returns `true` while a pan gesture is in progress (or has just
  // ended within the post-mouseup grace window). When set, both
  // `handleClick` and `handleFocus` early-return — pan is the user's
  // gesture intent, not a slot-selection. Without this guard, the
  // browser's focus-on-mousedown + click-after-mouseup events would
  // fire `setFocused` and `setDirection` for the wrong cell, leaving
  // the highlighted word out of sync with the actual focused cell.
  // Read as a getter (not a value) so the gesture state stays
  // synchronous; useGridNavigation re-evaluates on each event.
  readonly isPanning?: () => boolean;
  // Validation-set predicate; absent means no cell is validated.
  readonly isCellValidated?: (row: number, col: number) => boolean;
  // True while further input must be ignored — a full-grid validation is in flight and its verdict could be the win; blocking mutation keeps the submitted grid intact so a late keystroke can't corrupt the final state or void the winning verdict.
  readonly isInputLocked?: () => boolean;
  // Typing-advance skip: when this returns true, the cursor lands on the next still-empty cell in the word after a typed letter instead of the immediate next one. Absent ⇒ immediate next (legacy). Backspace/arrows unaffected.
  readonly skipFilledOnAdvance?: () => boolean;
}

// Formats the polite announcement emitted once each time the user enters a
// new word. Shape: « <clue text> », mot horizontal|vertical de N lettres :
// <slot pattern>  where each slot is either the current letter (uppercase)
// or the word 'point' for an empty cell (option-a format, comma-separated).
function formatWordEntryAnnouncement(
  clue: Clue,
  getEntryAt: (row: number, col: number) => string,
): string {
  const cells = clue.cells;
  const direction = cells.every((c) => c.position.row === cells[0]!.position.row)
    ? t('grid.wordEntry.direction.horizontal')
    : t('grid.wordEntry.direction.vertical');
  const pattern = cells
    .map((c) => {
      const letter = getEntryAt(c.position.row, c.position.col);
      return letter !== '' ? letter : t('grid.wordEntry.emptyCell');
    })
    .join(', ');
  return t('grid.sr.wordEntry', {
    clue: clue.clue.text,
    direction,
    length: cells.length,
    pattern,
  });
}

// With skip-filled on, land on the next still-empty cell in the word; fall back to the immediate next when every cell ahead is filled.
export function nextAdvanceIndex(
  cells: readonly LetterCell[],
  fromIdx: number,
  skipFilled: boolean,
  isFilled: (position: Position) => boolean,
): number | null {
  if (fromIdx < 0) return null;
  if (skipFilled) {
    for (let j = fromIdx + 1; j < cells.length; j++) {
      if (!isFilled(cells[j]!.position)) return j;
    }
  }
  return fromIdx < cells.length - 1 ? fromIdx + 1 : null;
}

export function useGridNavigation(puzzle: Puzzle, options?: UseGridNavigationOptions): GridNavigation {
  const lookup = useMemo(() => buildLookup(puzzle), [puzzle]);
  const refs = useRef(new Map<string, HTMLInputElement>());
  const [focused, setFocused] = useState<Position | null>(null);
  const [direction, setDirection] = useState<Direction>('across');
  // Stash in a ref so existing useCallback closures don't gain a new dep
  // (and so consumers passing an inline function don't churn handlers).
  const onCellChangeRef = useRef(options?.onCellChange);
  onCellChangeRef.current = options?.onCellChange;
  // Same ref pattern as `onCellChange` so an inline arrow at the call
  // site doesn't churn handler identities below.
  const onCellFilledRef = useRef(options?.onCellFilled);
  onCellFilledRef.current = options?.onCellFilled;
  // Same pattern for the presence-focus callback: refs keep handlers
  // stable so consumers passing inline functions don't trigger a fresh
  // mounting effect every render.
  const onFocusChangeRef = useRef(options?.onFocusChange);
  onFocusChangeRef.current = options?.onFocusChange;
  // Zoom-scale getter. Stashed in a ref so callers passing an inline
  // arrow (e.g. `() => wrapperRef.current?.state.scale ?? 1`) don't
  // churn `scheduleVisibleScroll`'s identity on every render.
  const getZoomScaleRef = useRef(options?.getZoomScale);
  getZoomScaleRef.current = options?.getZoomScale;
  // Pan-state getter. Stashed in a ref so consumers passing an inline
  // arrow don't churn the handler identities below.
  const isPanningRef = useRef(options?.isPanning);
  isPanningRef.current = options?.isPanning;
  // Validation-set predicate; ref so an inline arrow at the call site doesn't churn identities.
  const isCellValidatedRef = useRef(options?.isCellValidated);
  isCellValidatedRef.current = options?.isCellValidated;
  // Input-lock getter; ref for the same reason.
  const isInputLockedRef = useRef(options?.isInputLocked);
  isInputLockedRef.current = options?.isInputLocked;
  // Skip-filled-on-advance getter; ref for the same reason.
  const skipFilledOnAdvanceRef = useRef(options?.skipFilledOnAdvance);
  skipFilledOnAdvanceRef.current = options?.skipFilledOnAdvance;
  // Tracks the per-cell normalized (uppercase) value so handleInput can
  // detect same-letter no-ops. The browser overwrites target.value with the
  // raw IME character before handleInput fires, making a simple before/after
  // check on target.value unreliable for the Android insertText path.
  const cellValuesRef = useRef(new Map<string, string>());
  // `entriesVersion` is bumped on every write to `cellValuesRef`. The
  // hook's `getEntryAt` selector is memoized on the version so consumers
  // (the current-clue panel's letter previews) re-render when typed
  // letters change. Without this seam typed values would be stale on
  // the panel until the next focus event — fine for auto-advance words
  // but visibly broken on the last cell of a clue where focus stays.
  const [entriesVersion, setEntriesVersion] = useState(0);
  const bumpEntries = useCallback(() => setEntriesVersion((v) => v + 1), []);
  // State mirror so stable callbacks see the latest values.
  const stateRef = useRef({ focused, direction });
  stateRef.current = { focused, direction };
  const scrollTimeoutRef = useRef<number | null>(null);
  // Tracks the last cell the user clicked, separately from `focused`.
  // The toggle-on-repeat-click path needs to know whether the *previous
  // click* targeted the same cell — `focused` is sticky now, but iOS
  // soft-keyboard hide/reshow and stray pointer events can still
  // interleave focus changes between two same-cell clicks, so the
  // click history captures the user's actual interaction sequence
  // independent of focus churn. Cleared by `handleFocus` when focus
  // moves to a new cell via keyboard or programmatic navigation, so
  // the next tap is treated as a first click.
  const lastClickedRef = useRef<Position | null>(null);

  // Cancel any pending scroll on unmount so we don't fire scrollBy on a
  // detached input after the puzzle component is gone.
  useEffect(() => () => {
    if (scrollTimeoutRef.current !== null) {
      window.clearTimeout(scrollTimeoutRef.current);
    }
  }, []);

  // Mobile keyboard avoidance. Browsers normally auto-scroll a focused
  // <input> into view when the soft keyboard opens — but that path is
  // broken inside `TransformComponent`'s `overflow: hidden` wrapper:
  // the auto-scroll walks up the DOM looking for a scrollable ancestor,
  // finds the wrapper, "scrolls" it (no-op since it has no overflow),
  // and never reaches the document. We recover by computing where the
  // focused cell sits relative to `window.visualViewport` and calling
  // `window.scrollBy` with the delta needed to bring it inside the
  // visible region (above the keyboard, below the sticky clue panel).
  // This runs *after* a delay so the keyboard has had time to shrink
  // the visual viewport.
  const scheduleVisibleScroll = useCallback((input: HTMLInputElement) => {
    if (typeof window === 'undefined') return;
    if (scrollTimeoutRef.current !== null) {
      window.clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = window.setTimeout(() => {
      scrollTimeoutRef.current = null;
      if (!input.isConnected) return;
      // Skip the auto-scroll-into-view when the grid is pinch-zoomed.
      // The user has explicitly taken control of the in-grid viewport
      // (the library transforms the grid's content via JS-driven CSS
      // transform); auto-scrolling now yanks the page out from under
      // the cell they just zoomed to inspect. 1.01 (not strict 1.0)
      // absorbs floating-point jitter — `react-zoom-pan-pinch` has
      // been observed to leave `state.scale` at 1.0000000002 after
      // bounds-snap. Keyboard-avoidance (the original reason this
      // helper exists) is preserved: at scale === 1 (the steady state
      // unless the user has actively pinched in) the guard is a no-op.
      const zoomScale = getZoomScaleRef.current?.() ?? 1;
      if (zoomScale > 1.01) return;
      const rect = input.getBoundingClientRect();
      const vv = window.visualViewport;
      const vvTop = vv?.offsetTop ?? 0;
      const vvHeight = vv?.height ?? window.innerHeight;
      const visibleTop = vvTop + SCROLL_TOP_MARGIN_PX;
      const visibleBottom = vvTop + vvHeight - SCROLL_BOTTOM_MARGIN_PX;
      let dy = 0;
      // Bottom-overlap (cell behind keyboard) is the common case. Check
      // it first so a cell that sits both below the visible-bottom *and*
      // above the visible-top (a tiny visual viewport) prefers the
      // bottom-shift, which uncovers the cell from the keyboard.
      if (rect.bottom > visibleBottom) dy = rect.bottom - visibleBottom;
      else if (rect.top < visibleTop) dy = rect.top - visibleTop;
      if (dy !== 0) window.scrollBy({ top: dy, behavior: 'smooth' });
    }, SCROLL_DELAY_MS);
  }, []);

  const focusCell = useCallback((p: Position) => {
    refs.current.get(key(p))?.focus();
    // Sync mirror eagerly so re-entrant enterLetter calls in the same React tick see the updated position.
    stateRef.current = { ...stateRef.current, focused: p };
    setFocused(p);
  }, []);

  const registerCellRef = useCallback((el: HTMLInputElement | null) => {
    if (!el) return;
    const p = posOf(el);
    if (p) refs.current.set(key(p), el);
  }, []);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      // A click event arriving as the tail of a pan gesture isn't a
      // slot-selection. Bailing here keeps `direction` and
      // `lastClickedRef` exactly as they were before the gesture.
      if (isPanningRef.current?.() === true) return;
      const p = posOf(event.currentTarget);
      if (!p) return;
      // Skip only locked (validated) cells; touch makes every input read-only for caret suppression, which must not block selection.
      if (isCellValidatedRef.current?.(p.row, p.col)) return;
      // Read repeat-click state from `lastClickedRef` (NOT from `focused`):
      // iOS soft-keyboard hide/reshow can interleave focus changes
      // between two same-cell clicks even with sticky `focused`. The
      // click history captures the user's actual interaction sequence
      // independent of any focus churn that platform quirks introduce.
      const isRepeatClick = lastClickedRef.current !== null && same(lastClickedRef.current, p);
      lastClickedRef.current = p;
      const { direction: dir } = stateRef.current;
      const allClues = lookup.cluesAt(p.row, p.col);
      let next = dir;
      if (isRepeatClick) {
        // Same-cell repeat click — NYT-style toggle. Apply across ALL clues
        // at this cell, even if one of them starts here, so the user can
        // still reach the other clue (e.g. tapping the first cell of a
        // down word once focuses the down clue, tapping again toggles to
        // the across clue passing through that same cell).
        if (allClues.length === 1) next = allClues[0].direction;
        else if (allClues.length > 1) {
          const onCurrent = allClues.some((c) => c.direction === dir);
          if (onCurrent) next = dir === 'across' ? 'down' : 'across';
          else next = allClues[0].direction;
        }
      } else {
        // First click on this cell — pick a clue using a three-tier
        // priority. Tier 1 (smart-start): clues where the player has
        // already filled every prefix cell — `prefixFilled` returns
        // true only when there is at least one prefix cell, so a real-
        // start at `p` does NOT qualify. Tier 2 (real-start): the cell
        // is the first letter of the clue (structural word-boundary
        // preference). Tier 3: every clue passing through `p`. Smart-
        // start signals real progress and ranks above the merely
        // structural real-start, fixing the case where the global
        // current direction overrode obvious horizontal progress.
        const values = cellValuesRef.current;
        const smart = allClues.filter((c) => prefixFilled(c, p, values));
        let candidates: readonly Clue[];
        if (smart.length > 0) {
          candidates = smart;
        } else {
          const starting = allClues.filter((c) => same(c.cells[0].position, p));
          candidates = starting.length > 0 ? starting : allClues;
        }
        if (candidates.length === 1) {
          next = candidates[0].direction;
        } else if (candidates.length > 1) {
          const onCurrent = candidates.some((c) => c.direction === dir);
          next = onCurrent ? dir : candidates[0].direction;
        }
      }
      setDirection(next);
      // Focus the cell's input. The cell wrapper's mousedown handler
      // calls `event.preventDefault()` to stop the browser's default
      // blur of the active element, which also stops the default focus
      // path — so the input would never gain focus without this call.
      // Doing it here (and not on mousedown) means the focus only
      // moves on a real click (no drag), and the isPanning gate above
      // covers the synthesised post-pan click.
      refs.current.get(key(p))?.focus();
    },
    [lookup],
  );

  // First editable-and-empty cell (skip locked + typed), else first editable, else the word's first cell.
  const firstLandingCell = useCallback(
    (clue: Clue): Position => {
      const editable = clue.cells.filter(
        (c) => !(isCellValidatedRef.current?.(c.position.row, c.position.col) ?? false),
      );
      const firstEmpty = editable.find(
        (c) => (refs.current.get(key(c.position))?.value ?? '') === '',
      );
      return (firstEmpty ?? editable[0] ?? clue.cells[0]).position;
    },
    [],
  );

  const handleDefinitionClick = useCallback(
    (defPosition: Position) => {
      // Tail-of-pan synthesised clicks aren't slot selections — same gate as handleClick.
      if (isPanningRef.current?.() === true) return;
      const clues = lookup.cluesByDefCell(defPosition.row, defPosition.col);
      if (clues.length === 0) return;
      // Toggle: if the focused word is already one of this cell's clues, advance to the next; else start at the first.
      const { focused: f, direction: dir } = stateRef.current;
      const activeClue = f ? lookup.clueAt(f.row, f.col, dir) : undefined;
      const activeIdx = activeClue ? clues.findIndex((c) => c === activeClue) : -1;
      const target = clues[activeIdx >= 0 ? (activeIdx + 1) % clues.length : 0];
      if (target.direction !== dir) setDirection(target.direction);
      focusCell(firstLandingCell(target));
    },
    [firstLandingCell, focusCell, lookup],
  );

  const handleFocus = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    const p = posOf(event.currentTarget);
    if (p) {
      if (!same(stateRef.current.focused, p)) setFocused(p);
      // Keyboard/programmatic navigation lands here without a preceding handleClick,
      // so lastClickedRef still holds the old cell. Clear it so the next tap on
      // this cell is treated as a first click (starting-clue preference), not a toggle.
      if (!same(lastClickedRef.current, p)) lastClickedRef.current = null;
    }
    scheduleVisibleScroll(event.currentTarget);
  }, [scheduleVisibleScroll]);

  // Focus is intentionally sticky: tapping outside the grid leaves
  // `focused` set so highlight + clue panel + presence cursor persist.
  // A click on another letter cell still moves `focused` via that
  // cell's `handleFocus`. Grid is remounted on puzzle change
  // (key=refreshCount) so stale focus never leaks across puzzles.
  //
  // DOM focus is a separate concern: when the user taps a button, the
  // cell input itself blurs, which on mobile hides the soft keyboard
  // and on every platform makes typing unreachable. Snap DOM focus
  // back unless the blur is moving somewhere the user actually wants
  // it (another grid cell, another text input, a link). Synchronous
  // call so iOS Safari keeps us in the user-gesture context required
  // to re-show the soft keyboard.
  const handleBlur = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    if (!document.hasFocus()) return;
    const next = event.relatedTarget;
    if (next instanceof HTMLElement) {
      if (next.closest('[data-cell-kind="letter"]')) return;
      if (next instanceof HTMLInputElement) {
        const t = next.type;
        if (t === 'text' || t === 'search' || t === 'email' || t === 'password' || t === 'tel' || t === 'url' || t === 'number') return;
      }
      if (next instanceof HTMLTextAreaElement) return;
      if (next instanceof HTMLAnchorElement && next.href) return;
      if (next instanceof HTMLButtonElement) return;
      if (next instanceof HTMLSelectElement) return;
      if (next.isContentEditable) return;
    }
    event.currentTarget.focus({ preventScroll: true });
  }, []);

  const currentClue = useMemo<Clue | null>(
    () => (focused ? lookup.clueAt(focused.row, focused.col, direction) ?? null : null),
    [focused, direction, lookup],
  );

  // Cell-within-word index for the panel's "X / N" progress count.
  const currentClueIndex = useMemo<number | null>(() => {
    if (!focused || !currentClue) return null;
    const idx = currentClue.cells.findIndex((c) => same(c.position, focused));
    return idx >= 0 ? idx : null;
  }, [focused, currentClue]);

  // Alternate-direction clue at the same intersection, when present.
  // The lookup is direction-keyed so we just flip and re-query — same
  // path the keyboard space-bar / re-tap direction toggle takes.
  const alternateClue = useMemo<Clue | null>(() => {
    if (!focused) return null;
    const altDirection: Direction = direction === 'across' ? 'down' : 'across';
    return lookup.clueAt(focused.row, focused.col, altDirection) ?? null;
  }, [focused, direction, lookup]);

  const toggleDirection = useCallback(() => {
    setDirection((prev) => (prev === 'across' ? 'down' : 'across'));
  }, []);

  const getEntryAt = useCallback(
    (row: number, col: number): string =>
      cellValuesRef.current.get(key({ row, col })) ?? '',
    // The version counter is the load-bearing dep — it shifts on every
    // write to `cellValuesRef`, so the callback identity changes and
    // memo'd consumers (the clue panel) re-render with fresh letters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entriesVersion],
  );

  // Word-transition announcement. Fires exactly once each time the user
  // enters a new word (identified by the definition cell's position + arrow
  // direction). Moving within the same word skips the announcement; leaving
  // the grid clears the last-seen key so re-entering the same word announces
  // again (the user may have left and come back after reading the clue panel).
  const announcer = useAnnouncer();
  const lastWordKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!currentClue || !focused) {
      lastWordKeyRef.current = null;
      return;
    }
    const def = currentClue.definition.position;
    const wordKey = `${def.row}:${def.col}:${currentClue.clue.arrow}`;
    if (wordKey === lastWordKeyRef.current) return;
    lastWordKeyRef.current = wordKey;
    announcer.say(formatWordEntryAnnouncement(currentClue, getEntryAt));
  }, [currentClue, focused, announcer, getEntryAt]);

  // Fire the presence-focus callback on every (focused, direction)
  // transition. The transport-layer adapter (`WebSocketGameClient`) is
  // the single source of truth for the 200 ms debounce — this hook
  // surfaces every change verbatim. `null` focused fires `(null, null)`
  // so peers drop the cursor; that's intentional and matches the wire
  // shape (`presenceUpdated.row` is nullable per AsyncAPI). When
  // `focused` is non-null we always have a meaningful direction.
  useEffect(() => {
    const handler = onFocusChangeRef.current;
    if (!handler) return;
    if (focused === null) {
      handler(null, null);
      return;
    }
    handler(focused, direction);
  }, [focused, direction]);

  // Word range under the focused cell along the active direction. Pure
  // helper, same one PresenceOverlay uses to render remote cursors —
  // so the local "current word" tint and the remote-presence tints
  // stay byte-identical for any (position, direction) pair.
  const currentWordRange = useMemo<readonly Position[]>(
    () => (focused ? wordRange(puzzle, focused, direction) : []),
    [focused, direction, puzzle],
  );

  const moveByVector = useCallback(
    (dr: number, dc: number) => {
      const f = stateRef.current.focused;
      if (!f) return;
      let row = f.row + dr, col = f.col + dc;
      while (row >= 0 && row < puzzle.height && col >= 0 && col < puzzle.width) {
        // Skip validated (locked) letters too, so a run of solved cells doesn't trap the cursor — landing direct here keeps the jump in the arrow's direction.
        if (lookup.cellAt(row, col)?.kind === 'letter' && !isCellValidatedRef.current?.(row, col)) {
          // A block-sandwiched cell may hold a word on one axis only; if the active direction has none here, adopt the surviving one so the word highlights.
          const dir = stateRef.current.direction;
          if (!lookup.clueAt(row, col, dir)) {
            const other = dir === 'across' ? 'down' : 'across';
            if (lookup.clueAt(row, col, other)) setDirection(other);
          }
          return focusCell({ row, col });
        }
        row += dr; col += dc;
      }
    },
    [focusCell, lookup, puzzle.height, puzzle.width],
  );

  const enterLetter = useCallback(
    (char: string) => {
      if (isInputLockedRef.current?.()) return;
      const { focused: f, direction: dir } = stateRef.current;
      if (!f) return;
      const letter = stripDiacritics(char);
      if (letter.length !== 1 || !LETTER_RE.test(letter)) return;
      const el = refs.current.get(key(f));
      // Locked cells never mutate; touch read-only must still accept the on-screen keyboard, so gate on validation, not el.readOnly.
      if (el && !isCellValidatedRef.current?.(f.row, f.col)) {
        const before = el.value;
        el.value = letter;
        if (before !== letter) {
          cellValuesRef.current.set(key(f), letter);
          bumpEntries();
          onCellChangeRef.current?.(f.row, f.col, letter);
        }
        onCellFilledRef.current?.(f, dir);
      }
      const clue = lookup.clueAt(f.row, f.col, dir);
      if (!clue) return;
      const idx = clue.cells.findIndex((c) => same(c.position, f));
      const nextIdx = nextAdvanceIndex(
        clue.cells,
        idx,
        skipFilledOnAdvanceRef.current?.() ?? false,
        (pos) => (cellValuesRef.current.get(key(pos)) ?? '') !== '',
      );
      if (nextIdx !== null) focusCell(clue.cells[nextIdx]!.position);
    },
    [bumpEntries, focusCell, lookup],
  );

  const cycleClue = useCallback(
    (step: 1 | -1) => {
      const { focused: f, direction: dir } = stateRef.current;
      const clues = lookup.orderedClues;
      if (clues.length === 0) return;
      let nextClue: Clue;
      if (!f) {
        nextClue = step === -1 ? clues[clues.length - 1] : clues[0];
      } else {
        const here = lookup.cluesAt(f.row, f.col);
        const current = here.find((h) => h.direction === dir) ?? here[0];
        const currentIdx = current ? clues.indexOf(current) : -1;
        const baseIdx = currentIdx < 0 ? (step === -1 ? 0 : -1) : currentIdx;
        const nextIdx = (baseIdx + step + clues.length) % clues.length;
        nextClue = clues[nextIdx];
      }
      if (nextClue.direction !== stateRef.current.direction) setDirection(nextClue.direction);
      focusCell(firstLandingCell(nextClue));
    },
    [firstLandingCell, focusCell, lookup],
  );

  const eraseLetter = useCallback(() => {
    if (isInputLockedRef.current?.()) return;
    const { focused: f, direction: dir } = stateRef.current;
    if (!f) return;
    const clue = lookup.clueAt(f.row, f.col, dir);
    const idx = clue ? clue.cells.findIndex((c) => same(c.position, f)) : -1;
    const prev = clue && idx > 0 ? clue.cells[idx - 1].position : null;
    const el = refs.current.get(key(f));
    // Always step back after erasing so filled and empty cells behave identically.
    if (el && el.value !== '' && !isCellValidatedRef.current?.(f.row, f.col)) {
      el.value = '';
      cellValuesRef.current.delete(key(f));
      bumpEntries();
      onCellChangeRef.current?.(f.row, f.col, null);
      if (prev) focusCell(prev);
      return;
    }
    if (!prev) return;
    const prevEl = refs.current.get(key(prev));
    if (prevEl && !isCellValidatedRef.current?.(prev.row, prev.col)) {
      const before = prevEl.value;
      prevEl.value = '';
      if (before !== '') {
        cellValuesRef.current.delete(key(prev));
        bumpEntries();
        onCellChangeRef.current?.(prev.row, prev.col, null);
      }
    }
    focusCell(prev);
  }, [bumpEntries, focusCell, lookup]);

  // Flip to the pressed axis only when this cell has a word there; otherwise step straight.
  const moveCursor = useCallback(
    (direction: 'left' | 'right' | 'up' | 'down') => {
      const { focused: f, direction: dir } = stateRef.current;
      if (!f) return;
      if (direction === 'left' || direction === 'right') {
        if (dir !== 'across' && lookup.clueAt(f.row, f.col, 'across')) {
          setDirection('across');
          return;
        }
        moveByVector(0, direction === 'right' ? 1 : -1);
        return;
      }
      if (dir !== 'down' && lookup.clueAt(f.row, f.col, 'down')) {
        setDirection('down');
        return;
      }
      moveByVector(direction === 'down' ? 1 : -1, 0);
    },
    [moveByVector, lookup],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const { focused: f, direction: dir } = stateRef.current;
      const k = event.key;
      // Tab / Enter — cycle to the next (or, with Shift, previous) word's
      // first cell. Handled BEFORE the printable-letter branch so Enter
      // never falls through to letter-write logic (it isn't a letter, but
      // belt-and-braces) and BEFORE the `if (!f) return` guard so a Tab /
      // Enter with no current focus picks the first word's first cell.
      // preventDefault on Tab stops the browser walking out of the grid;
      // on Enter it stops form-submit-style side effects on hosts where
      // this hook is embedded in a form.
      if (k === 'Tab' || k === 'Enter') {
        event.preventDefault();
        cycleClue(event.shiftKey ? -1 : 1);
        return;
      }
      if (!f) return;
      if (k.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey && LETTER_RE.test(k)) {
        event.preventDefault();
        enterLetter(k);
        return;
      }
      switch (k) {
        // Space — flip solving direction at the current intersection
        // (matches NYT crossword + most French puzzle apps; the panel's
        // "Espace" kbd chip exposes the same shortcut visually).
        case ' ':
        case 'Spacebar': {
          event.preventDefault();
          const here = lookup.cluesAt(f.row, f.col);
          if (here.length < 2) return;
          setDirection((prev) => (prev === 'across' ? 'down' : 'across'));
          return;
        }
        case 'ArrowRight':
          event.preventDefault();
          moveCursor('right');
          return;
        case 'ArrowLeft':
          event.preventDefault();
          moveCursor('left');
          return;
        case 'ArrowDown':
          event.preventDefault();
          moveCursor('down');
          return;
        case 'ArrowUp':
          event.preventDefault();
          moveCursor('up');
          return;
        case 'Home': {
          event.preventDefault();
          const homeClue = lookup.clueAt(f.row, f.col, dir);
          if (!homeClue) return;
          focusCell(homeClue.cells[0].position);
          return;
        }
        case 'End': {
          event.preventDefault();
          const endClue = lookup.clueAt(f.row, f.col, dir);
          if (!endClue) return;
          focusCell(endClue.cells[endClue.cells.length - 1].position);
          return;
        }
        case 'Backspace': {
          event.preventDefault();
          eraseLetter();
          return;
        }
      }
    },
    [cycleClue, enterLetter, eraseLetter, focusCell, lookup, moveCursor],
  );

  const handleInput = useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      const inputEvent = event.nativeEvent as InputEvent;
      const target = event.currentTarget;
      // Locked (e.g. a full-grid validation in flight): the browser may have already written the raw glyph, so restore the cell to its mirror rather than just returning — a bare return would leave the stray letter in the DOM.
      if (isInputLockedRef.current?.()) {
        const p = posOf(target);
        const stored = p ? cellValuesRef.current.get(key(p)) ?? '' : '';
        if (target.value !== stored) target.value = stored;
        return;
      }
      // Validated cells are read-only. Browsers block native input
      // here, but Android soft keyboards have been observed to bypass
      // readOnly via composition events — mirror the keydown path's
      // guard so a typed letter never lands on a locked cell.
      if (target.readOnly) return;
      // Non-insert input events split two ways:
      //   1. Paste / autocorrect — multi-char or non-letter content lands
      //      in `target.value`. Blank it and notify (always — even if the
      //      mirror was already empty, consumers may need to know a paste
      //      was rejected because server-side state could differ).
      //   2. Mobile erase — Gboard / iOS fire `deleteContentBackward` here
      //      when erasing a filled cell. The browser already cleared the
      //      DOM by the time we run, so we can't diff `target.value`.
      //      Reconcile against the cell-values mirror instead.
      // Desktop Backspace doesn't reach here; handleKeyDown's
      // `case 'Backspace'` preventDefaults and keeps the mirror in sync
      // itself. The empty-cell mobile case still arrives via keydown
      // (browser has nothing to delete natively, so it falls through).
      if (inputEvent.inputType !== 'insertText') {
        const p = posOf(target);
        if (target.value.length > 1 || (target.value && !LETTER_RE.test(target.value))) {
          target.value = '';
          if (p) {
            cellValuesRef.current.delete(key(p));
            bumpEntries();
            onCellChangeRef.current?.(p.row, p.col, null);
          }
          return;
        }
        if (target.value === '' && p && cellValuesRef.current.has(key(p))) {
          cellValuesRef.current.delete(key(p));
          bumpEntries();
          onCellChangeRef.current?.(p.row, p.col, null);
        }
        return;
      }
      const data = inputEvent.data;
      // Mobile (Gboard/iOS) delivers space as InputEvent.data, not keydown — flip direction here too.
      if (data === ' ') {
        const p = posOf(target);
        // Restore target.value from the cell mirror — space must not land in the DOM.
        const stored = p ? cellValuesRef.current.get(key(p)) ?? '' : '';
        if (target.value !== stored) target.value = stored;
        if (!p) return;
        const here = lookup.cluesAt(p.row, p.col);
        if (here.length < 2) return;
        setDirection((prev) => (prev === 'across' ? 'down' : 'across'));
        return;
      }
      if (!data || data.length !== 1 || !LETTER_RE.test(data)) {
        const before = target.value;
        target.value = '';
        if (before !== '') {
          const p = posOf(target);
          if (p) {
            cellValuesRef.current.delete(key(p));
            bumpEntries();
            onCellChangeRef.current?.(p.row, p.col, null);
          }
        }
        return;
      }
      const letter = stripDiacritics(data);
      target.value = letter;
      // Identify the destination cell from the input event's target —
      // the element that actually received the keystroke — rather than
      // from React's `focused` state. Focus state can lag during a fast
      // typing burst (auto-advance queues a setFocused that hasn't
      // committed yet by the time the next input event fires), and the
      // target is the authoritative source for "which cell was typed
      // into".
      const p = posOf(target);
      if (!p) return;
      const { direction: dir } = stateRef.current;
      const prevLetter = cellValuesRef.current.get(key(p)) ?? '';
      if (prevLetter !== letter) {
        cellValuesRef.current.set(key(p), letter);
        bumpEntries();
        onCellChangeRef.current?.(p.row, p.col, letter);
      }
      // Fill seam for per-word callers; fired on every keystroke that lands a letter, even a same-letter re-type.
      onCellFilledRef.current?.(p, dir);
      const clue = lookup.clueAt(p.row, p.col, dir);
      if (!clue) return;
      const idx = clue.cells.findIndex((c) => same(c.position, p));
      const nextIdx = nextAdvanceIndex(
        clue.cells,
        idx,
        skipFilledOnAdvanceRef.current?.() ?? false,
        (pos) => (cellValuesRef.current.get(key(pos)) ?? '') !== '',
      );
      if (nextIdx !== null) focusCell(clue.cells[nextIdx]!.position);
    },
    [bumpEntries, focusCell, lookup],
  );

  const highlightFor = useCallback(
    (p: Position): CellHighlight => {
      if (!focused || !currentClue) return { currentWord: false, focused: false, currentArrow: null };
      const isFocused = same(focused, p);
      // `currentWordRange` is computed via the same `wordRange` helper
      // PresenceOverlay uses for remote cursors — both code paths now
      // see the same range for any (position, direction) pair.
      const inWord = currentWordRange.some((q) => q.row === p.row && q.col === p.col);
      const def = currentClue.definition.position;
      const isDef = def.row === p.row && def.col === p.col;
      return {
        currentWord: inWord && !isFocused,
        focused: isFocused,
        currentArrow: isDef ? currentClue.clue.arrow : null,
      };
    },
    [currentClue, currentWordRange, focused],
  );

  // Inbound remote-update path. Mirrors the local-write side-effects on the
  // DOM (set `el.value`, sync the per-cell mirror) WITHOUT firing
  // `onCellChange` and WITHOUT touching focus/direction — see the field
  // comment on `GridNavigation.applyRemoteCellUpdate` for the full rationale.
  const applyRemoteCellUpdate = useCallback((row: number, col: number, letter: string | null) => {
    const k = key({ row, col });
    const el = refs.current.get(k);
    if (!el) return;
    const value = letter ?? '';
    el.value = value;
    if (value === '') cellValuesRef.current.delete(k);
    else cellValuesRef.current.set(k, value);
    bumpEntries();
  }, [bumpEntries]);

  const localCursor = focused
    ? { position: focused, direction }
    : null;

  // Stable accessor; reads through to the live ref so callers see the parent's current validation set.
  const isCellValidated = useCallback(
    (row: number, col: number) => isCellValidatedRef.current?.(row, col) ?? false,
    [],
  );

  return {
    registerCellRef,
    highlightFor,
    handleClick,
    handleDefinitionClick,
    handleFocus,
    handleBlur,
    handleKeyDown,
    handleInput,
    currentClue,
    currentClueIndex,
    alternateClue,
    toggleDirection,
    enterLetter,
    eraseLetter,
    cycleClue,
    moveCursor,
    isCellValidated,
    getEntryAt,
    localCursor,
    applyRemoteCellUpdate,
    allClues: lookup.orderedClues,
  };
}
